import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Col, Container, FormControl, Row } from 'react-bootstrap';
import { PersonCircle } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import FormGenerator from '../../form-generator/FormGenerator';
import { getDateFromInputString, getFormattedDate, highlightError } from '../../form-generator/helpers/utility';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS, ADMIN_ROLE, PROCESSOR_ROLE } from '../../helpers/constants';
import { changePasswordForm } from '../../helpers/forms';
import { saveUserToLocal } from '../../helpers/session';
import Heading from '../Heading';
import Loader from '../Loader';
import OverViewColumns from '../OverViewColumns';
import RichTextEditor from '../common/RichTextEditor';
import { isStaff } from '../../helpers/global';

const ChangePasswordForm = ({ showProgress, error }) => {
  return (
    <>
      <FormGenerator formJson={changePasswordForm} showProgress={showProgress} />
      {error && (
        <Alert style={{ fontSize: 14 }} className="mx-2 p-2" variant="danger">
          {error}
        </Alert>
      )}
    </>
  );
};

const commonFields = [
  { label: 'Name', key: 'name' },
  { label: 'Email', key: 'email' },
  { label: 'Phone Number', key: 'phone' },
  { label: 'Role', key: 'role' }
];

const commonEditFields = [
  { label: 'Name', key: 'name' },
  { label: 'Phone Number', key: 'phone' }
];

// Append inline style to enforce margin-bottom: 0 !important on all <p> tags
const addMb0ToParagraphs = html => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('p').forEach(p => {
    const styleAttr = p.getAttribute('style') || '';
    const needsSemicolon = styleAttr.trim() && !styleAttr.trim().endsWith(';');
    const combined = (styleAttr ? `${styleAttr}${needsSemicolon ? ';' : ''} ` : '') + 'margin: 0 !important;';
    p.setAttribute('style', combined);
  });
  return container.innerHTML;
};

const Profile = ({ onCurrentUserInfoChange }) => {
  const [role] = useState(localStorage.getItem('user-role'));
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState();
  const [editMode, setEditMode] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [savingProfile, setSubmittingProfile] = useState(false);

  const getProfile = useCallback(async () => {
    setLoadingProfile(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.USERS_ME,
        method: 'GET'
      });
      setLoadingProfile(false);
      if (error) {
        toast.error(error);
        return;
      }
      setEditingProfile(response);
    } catch (e) {
      setLoadingProfile(false);
      toast.error('Something went wrong! Please try again');
      console.log(e);
    }
  }, []);

  useEffect(() => {
    getProfile();
  }, [getProfile]);

  const onChangePasswordFormSubmit = async form => {
    const oldPassword = form['oldPassword'];
    const newPassword = form['newPassword'];
    const confirmPassword = form['confirmPassword'];

    if (newPassword !== confirmPassword) {
      highlightError(document.getElementById('confirmPassword'), 'Passwords do not match');
      return;
    }

    setFormSubmitting(true);

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CHANGE_PASSWORD,
        requestBody: { oldPassword, newPassword, confirmPassword }
      });

      setFormSubmitting(false);
      if (error) {
        toast.error(error);
        return;
      }

      ['oldPassword', 'newPassword', 'confirmPassword'].forEach(id => {
        try {
          document.getElementById(id).value = '';
        } catch (error) { }
      });
      toast.success('Password updated successfully!');
    } catch (e) {
      setFormSubmitting(false);
      toast.error('Something went wrong! Please try again');
      console.log(e);
    }
  };

  window['onChangePasswordFormSubmit'] = onChangePasswordFormSubmit;

  const onCancelProfileEdit = () => {
    getProfile();
    setEditMode(false);
  };

  const onSaveProfileClick = async () => {
    const isSomeFieldEmpty = commonFields.some(f => !editingProfile[f.key]);

    if (isSomeFieldEmpty) {
      return toast.error('Please fill all the fields!');
    }

    const requestBody = { _id: editingProfile._id };
    commonFields.forEach(f => {
      requestBody[f.key] = editingProfile[f.key];
    });
    // Include signature in the save
    if (editingProfile.signature !== undefined) {
      requestBody.signature = addMb0ToParagraphs(editingProfile.signature || '');
    }

    setSubmittingProfile(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.USERS_UPDATE,
        requestBody:
          role === ADMIN_ROLE || role === PROCESSOR_ROLE
            ? { ...requestBody }
            : { ...requestBody, organization: user?.organization?._id }
      });

      setSubmittingProfile(false);

      if (error) {
        toast.error(error);
        return;
      }
      saveUserToLocal(response, false);
      setEditingProfile({ ...response });
      onCurrentUserInfoChange({ ...response }); // Updating State For Real Time User Name Update In Header Section
      setEditMode(false);
      toast.success('Profile updated successfully!');
    } catch (e) {
      setSubmittingProfile(false);
      toast.error('Something went wrong! Please try again');
      console.log(e);
    }
  };

  return (
    <Container className="px-0 px-md-5 py-3 bg-white" fluid>
      <Row>
        <Col xs={12} className="px-4 px-md-5">
          <Card className="fade-in ">
            <Card.Body className="left-bar rounded">
              <div className="d-flex">
                <div className="d-none d-md-block px-2 py-3 text-dark">
                  <PersonCircle size={50} />
                </div>
                <div className="d-md-none px-1 py-3 text-dark">
                  <PersonCircle size={35} />
                </div>
                <div className="flex-grow-1 bg-white px-4">
                  {loadingProfile ? (
                    <Loader />
                  ) : (
                    editingProfile && (
                      <>
                        <Heading
                          title={'Profile Details'}
                          withEditToggle
                          editMode={editMode}
                          onEditClick={() => setEditMode(true)}
                          onCancelClick={onCancelProfileEdit}
                          onSaveClick={onSaveProfileClick}
                          inProgress={savingProfile}
                        />
                        {!editMode ? (
                          <>
                            <OverViewColumns
                              md={6}
                              className="m-1 mb-4"
                              fieldsToShow={commonFields}
                              data={editingProfile}
                            />
                          </>
                        ) : (
                          <Row className="m-1 mb-4">
                            {commonEditFields.map(({ label, key, type = 'text' }) => {
                              return (
                                <Col xs={12} md={6} key={key} className="mt-2  px-2">
                                  <h6 className="mb-1 text-dark ">
                                    <span className="text-muted mid">{label}: </span>
                                  </h6>
                                  <FormControl
                                    size="sm"
                                    className="mb-0  text-dark px-2 py-1 large rounded"
                                    type={type}
                                    value={
                                      type === 'date'
                                        ? editingProfile[key]
                                          ? getFormattedDate(editingProfile[key])
                                          : ''
                                        : editingProfile[key]
                                    }
                                    onChange={e =>
                                      setEditingProfile({
                                        ...editingProfile,
                                        [key]: type === 'date' ? getDateFromInputString(e.target.value) : e.target.value
                                      })
                                    }
                                  />
                                </Col>
                              );
                            })}
                          </Row>
                        )}

                        {isStaff(role) && (
                          <>
                            <Heading title={'Email Signature'} className="mt-4" />
                            <div className="px-2" style={{ marginBottom: '4.5rem' }}>
                              <p className="text-muted small mb-2">
                                This signature will be automatically added to emails you send from the app.
                              </p>
                              <div style={{ marginBottom: '1.5rem', position: 'relative', zIndex: 1 }}>
                                {editMode ? (
                                  <RichTextEditor
                                    value={editingProfile.signature || ''}
                                    onChange={value =>
                                      setEditingProfile({
                                        ...editingProfile,
                                        signature: value
                                      })
                                    }
                                    placeholder="Enter your email signature here..."
                                    height="200px"
                                    enableColor={true}
                                    enableLineHeight={true}
                                    enableFontSize={true}
                                  />
                                ) : editingProfile.signature ? (
                                  <div
                                    className="border rounded p-3"
                                    style={{ minHeight: '200px', backgroundColor: '#f8f9fa' }}
                                    dangerouslySetInnerHTML={{ __html: addMb0ToParagraphs(editingProfile.signature) }}
                                  />
                                ) : (
                                  <div
                                    className="border rounded p-3"
                                    style={{ minHeight: '200px', backgroundColor: '#f8f9fa' }}
                                  >
                                    <p className="text-muted mb-0">No signature set. Click Edit above to add one.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <Heading
                          title={'Password Change'}
                          style={{ marginTop: '2.5rem', position: 'relative', zIndex: 2 }}
                        />
                        <ChangePasswordForm showProgress={formSubmitting} />
                      </>
                    )
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
